import React from 'react';
import { mapValues, pickBy, omit, some } from 'lodash';
import PropTypes from 'prop-types';
import { defaultMemoize } from 'reselect';
import { nucleusFormShape } from '../NucleusForm';
import shallowObjectEqual from '../utils/shallowObjectEqual';
import Subscription from '../utils/Subscription';
import { addSuppressErrorsEventHandler, removeSuppressErrorsEventHandler } from '../utils/suppressErrors';
const hasErrors = inputInformation => {
    const { validationResults = {} } = inputInformation;
    return some(validationResults, result => !result.isValid);
};
const getErrorMessages = (value, label, validations = {}, validationResults = {}) => {
    return pickBy(mapValues(validationResults, (result, validationKey) => {
        const validation = validations[validationKey];
        if (validation && !result.isValid) {
            const { errorMessage } = validation;
            return typeof errorMessage === 'function' ? errorMessage(value, label) : errorMessage;
        }
    }));
};
const wrapInputWithErrors = options => Component => {
    var _a;
    return _a = class ErrorMessages extends React.Component {
            constructor(props, context) {
                super(props, context);
                /*
                 * Handles when errors should be suppressed. The component will be marked
                 * as suppressing errors if errors were not already visible.
                 */
                this.handleSuppressErrorsEvent = suppressErrors => {
                    const errorsCurrentlySuppressed = this.suppressErrors;
                    const shouldSuppressErrors = suppressErrors && !this.showErrors(this.state.inputInformation);
                    if (shouldSuppressErrors !== this.suppressErrors) {
                        this.suppressErrors = shouldSuppressErrors;
                    }
                    const errorsWereSuppressed = errorsCurrentlySuppressed && !this.suppressErrors && this.showErrors(this.state.inputInformation);
                    if (errorsWereSuppressed) {
                        // NUKE-845 There was an issue with continuing to suppress errors after a failed submit. To remove the
                        // race condition, suppressErrors was pulled out of component state to be more atomic. To still ensure
                        // a render, forceUpdate is called.
                        this.forceUpdate();
                    }
                };
                this.generateInputInformation = () => {
                    const { [options.inputIdKey]: inputId } = this.props;
                    return Object.assign({ isFailedSubmit: this.nucleusForm.selectors.getForm().isFailedSubmit, failedSubmitCount: this.nucleusForm.selectors.getForm().failedSubmitCount }, this.nucleusForm.selectors.getInput(inputId));
                };
                this.setInputInformation = () => {
                    const inputInformation = this.generateInputInformation();
                    if (!shallowObjectEqual(this.state.inputInformation, inputInformation)) {
                        // During a submit it's possible for components to render prior to the
                        // submit successful action firing. This can lead to a component which
                        // has an initialFailedSubmitCount higher than intended. Since the
                        // count is only higher than zero if isFailedSubmit is true,
                        // the count can be reset. Not resetting leads to errors which do not
                        // display until several submit attempts.
                        if (!inputInformation.isFailedSubmit) {
                            this.initialFailedSubmitCount = inputInformation.failedSubmitCount;
                        }
                        this.setState({
                            inputInformation
                        });
                    }
                };
                this.showErrors = input => {
                    const isFailedSubmit = input.isFailedSubmit && input.failedSubmitCount > this.initialFailedSubmitCount;
                    return (isFailedSubmit || input.isTouched) && !input.isFocusedDirty;
                };
                // Memoize the getErrorMessages so it doesn't generate a new version if
                // the props it requires do not change.
                this.getErrorMessages = defaultMemoize(getErrorMessages);
                this.hasErrors = defaultMemoize(hasErrors);
                this.nucleusForm = props.nucleusForm || context.nucleusForm;
                this.subscription = new Subscription(this.nucleusForm.store, this.setInputInformation);
                this.initialFailedSubmitCount = this.nucleusForm.selectors.getForm().failedSubmitCount;
                this.state = {
                    inputInformation: this.generateInputInformation()
                };
                this.suppressErrors = false;
                this.nucleusForm.actions.addInput(props[options.inputIdKey]);
                if (this.nucleusForm) {
                    this.subscription.trySubscribe();
                }
            }
            componentDidMount() {
                addSuppressErrorsEventHandler(this.handleSuppressErrorsEvent);
            }
            componentWillUnmount() {
                if (this.nucleusForm) {
                    this.subscription.tryUnsubscribe();
                }
                removeSuppressErrorsEventHandler(this.handleSuppressErrorsEvent);
            }
            shouldComponentUpdate(_, nextState) {
                // No need to update if hidden errors should continue to be suppressed. This helps
                // fix an issue with firefox where select boxes lose focus when this higher order
                // component renders.
                const transitionToSuppressingErrors = !this.suppressErrors && nextState.suppressErrors;
                return !transitionToSuppressingErrors;
            }
            render() {
                const { includeErrorMessages, includeHasErrors } = this.props;
                const passDownErrorInformation = includeErrorMessages || includeHasErrors;
                const componentProps = omit(this.props, ['includeErrorMessages', 'includeHasErrors']);
                if (!this.nucleusForm || !passDownErrorInformation) {
                    return React.createElement(Component, Object.assign({}, componentProps), this.props.children);
                }
                const { [options.valueKey]: value, [options.labelKey]: label, validations } = this.props;
                const displayErrors = !this.suppressErrors && this.showErrors(this.state.inputInformation);
                const inputHasErrors = displayErrors && this.hasErrors(this.state.inputInformation);
                const errorMessages = inputHasErrors && includeErrorMessages ? this.getErrorMessages(value, label, validations, this.state.inputInformation.validationResults) : undefined;
                if (errorMessages) {
                    componentProps.errorMessages = errorMessages;
                }
                if (includeHasErrors) {
                    componentProps.hasErrors = inputHasErrors;
                }
                return React.createElement(Component, Object.assign({}, componentProps), this.props.children);
            }
        },
        _a.displayName = `InputErrors(${Component.displayName})`,
        _a.contextTypes = {
            nucleusForm: nucleusFormShape
        },
        _a.propTypes = {
            [options.inputIdKey]: PropTypes.string.isRequired,
            includeHasErrors: PropTypes.bool,
            includeErrorMessages: PropTypes.bool,
            validations: PropTypes.objectOf(PropTypes.shape({
                errorMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
            })),
            children: PropTypes.node,
            nucleusForm: nucleusFormShape
        },
        _a.defaultProps = {
            includeHasErrors: options.defaultIncludeHasErrors,
            includeErrorMessages: options.defaultIncludeErrorMessages
        },
        _a;
};
export default wrapInputWithErrors;
